import * as THREE from 'three/webgpu';
import {
    ScreenSpaceDashedLineNodeMaterial,
    syncScreenSpaceDashedLineGeometry,
} from '../../material/ScreenSpaceDashedLineNodeMaterial';

/** 测量线屏幕像素线宽（较点捕捉辅助线默认值加宽一倍）。 */
export const MEASURE_LINE_WIDTH_PX = 4; // webgl线的宽度只支持1px 这里设置多少都不起作用

/** 测量虚线段屏幕像素长度。 */
export const MEASURE_DASH_SIZE_PX = 7;

/** 测量虚线间隔屏幕像素长度。 */
export const MEASURE_GAP_SIZE_PX = 4;

/** 测量屏空间虚线对象类型。 */
export type MeasureScreenSpaceDashedLine = THREE.Line<
    THREE.BufferGeometry,
    ScreenSpaceDashedLineNodeMaterial
>;

/**
 * 创建测量用屏幕空间虚线对象（两端点线段）。
 *
 * 设计说明：
 * 1. 使用 {@link ScreenSpaceDashedLineNodeMaterial}，虚线 dash/gap 以屏幕像素计，缩放视图时疏密稳定；
 * 2. 每条线独享材质实例（材质内部持有 points uniform）；
 * 3. 关闭深度测试，由调用方设置 renderOrder 保证叠在模型之上；
 * 4. 几何写入后必须调用 {@link syncScreenSpaceDashedLineGeometry}。
 *
 * @param color 线条颜色。
 * @param renderOrder 渲染排序。
 * @param name 对象名称。
 * @returns 屏空间虚线 Line 对象。
 */
export function createMeasureDashedLine(
    color: number,
    renderOrder: number,
    name: string,
): MeasureScreenSpaceDashedLine {
    const positions = new Float32Array(6);
    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.computeBoundingSphere();

    // 初始给合法两点，保证 Node 变体能建立 screen-distance 计算图
    const material = new ScreenSpaceDashedLineNodeMaterial({
        color,
        linewidth: MEASURE_LINE_WIDTH_PX,
        dashSize: MEASURE_DASH_SIZE_PX,
        gapSize: MEASURE_GAP_SIZE_PX,
        transparent: true,
        opacity: 1,
        points: [
            new THREE.Vector3(0, 0, 0),
            new THREE.Vector3(1, 0, 0),
        ],
    });
    material.depthTest = false;
    material.depthWrite = false;

    const line = new THREE.Line<THREE.BufferGeometry, ScreenSpaceDashedLineNodeMaterial>(
        geometry,
        material,
    );
    line.name = name;
    line.frustumCulled = false;
    line.renderOrder = renderOrder;
    line.userData.skipSelection = true;
    line.raycast = () => undefined;
    syncScreenSpaceDashedLineGeometry(line);
    return line;
}

/**
 * 写入测量虚线两端世界坐标并同步屏空间虚线几何依赖。
 *
 * @param line 目标虚线。
 * @param start 起点。
 * @param end 终点。
 */
export function writeMeasureDashedLinePositions(
    line: MeasureScreenSpaceDashedLine,
    start: THREE.Vector3,
    end: THREE.Vector3,
): void {
    const positionAttribute = line.geometry.getAttribute('position') as THREE.BufferAttribute;
    positionAttribute.setXYZ(0, start.x, start.y, start.z);
    positionAttribute.setXYZ(1, end.x, end.y, end.z);
    positionAttribute.needsUpdate = true;
    line.geometry.computeBoundingSphere();
    // 同步 points uniform 与 lineDistancePercent；屏幕距离在 shader 内按当前相机每帧重算
    syncScreenSpaceDashedLineGeometry(line);
}
